import { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { usePlanLimitContent } from '../help/planLimitContent';

const toDateInput = (date) => date.toISOString().slice(0, 10);

const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: toDateInput(from), to: toDateInput(to) };
};

// Every figure comes from GET /metrics/reports?companyId=&from=&to=&type= —
// the date-range and type controls only change the request's query params,
// never filter an already-fetched set client-side (CHANGES.md decision 4).
const useReportsPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');
  const planLimit = usePlanLimitContent('reports.generated.monthly');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const initialRange = defaultRange();
  const [fromInput, setFromInput] = useState(initialRange.from);
  const [toInput, setToInput] = useState(initialRange.to);
  const [reportType, setReportType] = useState('sales');
  const [range, setRange] = useState(initialRange);

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [data, setData] = useState(null);

  const fetchMetrics = useCallback(() => {
    if (!config?.backendUrl || !companyId) return;
    setStatus('loading');
    const params = {
      companyId,
      from: `${range.from}T00:00:00`,
      to: `${range.to}T23:59:59`,
      type: reportType,
    };
    axios.get(`${config.backendUrl}/metrics/reports`, { params })
      .then((res) => {
        setData(res.data);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching report metrics', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /metrics/reports · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      });
  }, [config, companyId, range, reportType, t]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const runReport = () => setRange({ from: fromInput, to: toInput });

  const effectiveStatus = previewState || status;

  return {
    t, companyId, effectiveStatus, data, errorInfo, fetchMetrics,
    fromInput, setFromInput, toInput, setToInput, reportType, setReportType, runReport,
    planLimit,
  };
};

export default useReportsPage;
